import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { LockIcon } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { PIN_LENGTH, PinPad } from '@/components/pin-pad';
import { Button } from '@/components/ui/button';
import { Alert } from '@/components/ui/form';
import { biometricUnlocksCache } from '@/lib/auth/biometric';
import { useAuth } from '@/lib/auth/context';
import { grantRemainingMs, hardWipe } from '@/lib/session';

export const Route = createFileRoute('/kunci')({
  component: LockScreen,
});

/**
 * Layar kunci offline (T-P7-06, FR-AUTH-08).
 *
 * Percobaan gagal dibatasi di sini, bukan di server: perangkat yang terkunci
 * justru yang sedang offline, dan tanpa batas lokal PIN 6 digit dapat ditebak
 * habis dalam hitungan jam oleh siapa pun yang memegang ponselnya. Batas itu
 * berakhir dengan menghapus cache — data nasabah tidak boleh jadi taruhannya.
 */
const MAX_ATTEMPTS = 5;

function LockScreen() {
  const { status, envelope, unlockWithPin, unlockWithFingerprint } = useAuth();
  const navigate = useNavigate();

  const [pin, setPin] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [biometricReady, setBiometricReady] = useState(false);

  useEffect(() => {
    if (status === 'authenticated') void navigate({ to: '/', replace: true });
    if (status === 'anonymous' || status === 'expired')
      void navigate({ to: '/masuk', replace: true });
  }, [status, navigate]);

  useEffect(() => {
    void biometricUnlocksCache().then(setBiometricReady);
  }, []);

  const tryBiometric = useCallback(async () => {
    setBusy(true);
    const opened = await unlockWithFingerprint();
    setBusy(false);
    if (!opened) setError('Biometrik tidak dikenali. Gunakan PIN.');
  }, [unlockWithFingerprint]);

  // Sensor dipanggil sekali secara otomatis: itu jalur tercepat, dan kegagalannya
  // tidak menghalangi apa pun karena papan PIN tetap tampil di belakangnya.
  useEffect(() => {
    if (biometricReady) void tryBiometric();
  }, [biometricReady, tryBiometric]);

  async function submit() {
    if (pin.length !== PIN_LENGTH || busy) return;

    setBusy(true);
    const opened = await unlockWithPin(pin);
    setBusy(false);
    setPin('');

    if (opened) return;

    const next = attempts + 1;
    setAttempts(next);

    if (next >= MAX_ATTEMPTS) {
      await hardWipe();
      void navigate({ to: '/masuk', replace: true });
      return;
    }

    setError(`PIN salah. Sisa ${MAX_ATTEMPTS - next} percobaan sebelum data lokal dihapus.`);
  }

  const remainingHours = envelope ? Math.floor(grantRemainingMs(envelope) / 3_600_000) : 0;

  return (
    <main className="flex min-h-dvh flex-col justify-center gap-8 px-6 py-10">
      <header className="space-y-2 text-center">
        <LockIcon className="mx-auto size-7 text-muted-foreground" aria-hidden="true" />
        <h1 className="text-lg font-semibold">{envelope?.username ?? 'Perangkat terkunci'}</h1>
        <p className="text-sm text-muted-foreground">
          {remainingHours > 0
            ? `Akses offline berlaku ${remainingHours} jam lagi sebelum wajib login online.`
            : 'Akses offline hampir habis — sambungkan ke jaringan setelah membuka kunci.'}
        </p>
      </header>

      {attempts >= MAX_ATTEMPTS - 2 && attempts > 0 ? (
        <Alert variant="warning">
          Cache lokal akan dihapus setelah {MAX_ATTEMPTS} percobaan gagal. Data yang belum terkirim
          ikut hilang.
        </Alert>
      ) : null}

      <PinPad
        value={pin}
        onChange={(next) => {
          setError(null);
          setPin(next);
        }}
        onSubmit={() => void submit()}
        onBiometric={biometricReady ? () => void tryBiometric() : undefined}
        disabled={busy}
        label="Masukkan PIN"
        error={error}
      />

      <Button variant="ghost" fullWidth onClick={() => void navigate({ to: '/masuk' })}>
        Masuk dengan password
      </Button>
    </main>
  );
}
